import React, { useState } from "react";
import { z } from "zod";
import { createEnrollment } from "../../services/api";

const QUALIFICATIONS = [
  "10th Pass",
  "12th Pass",
  "Diploma",
  "B.Tech/B.E.",
  "B.Sc",
  "B.Com",
  "B.A",
  "BCA",
  "MCA",
  "M.Tech/M.E.",
  "MBA",
  "M.Sc",
  "Ph.D",
  "Other",
];

const enrollSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\d{10}$/, "Phone must be exactly 10 digits"),
  qualification: z.string().min(1, "Qualification is required"),
  location: z.string().min(1, "Location is required"),
  preference: z.string().min(1, "Preference is required"),
});

const EnrollModal = ({ course, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    qualification: "",
    location: "",
    preference: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const result = enrollSchema.safeParse(form);
    if (!result.success) {
      const errs = {};
      result.error.issues.forEach((e) => { errs[e.path[0]] = e.message; });
      setFieldErrors(errs);
      setError(result.error.issues[0].message);
      return;
    }
    setLoading(true);
    setError("");
    try {
      await createEnrollment({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        qualification: form.qualification,
        location: form.location.trim(),
        preference: form.preference.trim(),
        courseId: course.id,
      });
      setSuccess(true);
    } catch (e) {
      setError(
        e.response?.data?.message || "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay open"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-box"
        style={{ maxWidth: 500 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>
          X
        </button>

        {success ? (
          <div style={{ textAlign: "center", padding: "28px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🎉</div>
            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: 22,
                fontWeight: 800,
                marginBottom: 10,
              }}
            >
              Enrollment Submitted!
            </div>
            <p
              style={{
                color: "var(--text2)",
                fontSize: 14,
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              We will contact you at <strong>{form.email}</strong> with course
              access details.
            </p>
            <button
              className="btn-primary"
              onClick={onClose}
              style={{ padding: "12px 32px" }}
            >
              Done
            </button>
          </div>
        ) : (
          <div>
            {/* Course info strip */}
            <div
              style={{
                background: "var(--surface)",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 20,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "var(--accent)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  flexShrink: 0,
                }}
              >
                {course.emoji || "📚"}
              </div>
              <div>
                <div
                  style={{
                    fontFamily: "Syne,sans-serif",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  {course.title}
                </div>
                <div style={{ color: "var(--text3)", fontSize: 12 }}>
                  {course.provider ? course.provider.name : ""}
                  {course.level ? " · " + course.level : ""}
                  {course.price === 0
                    ? " · Free"
                    : course.price
                      ? " · Rs." + course.price
                      : ""}
                </div>
              </div>
            </div>

            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 18,
              }}
            >
              📋 Enroll in this Course
            </div>

            {error && (
              <div
                style={{
                  color: "var(--accent2)",
                  fontSize: 13,
                  marginBottom: 14,
                  padding: "8px 12px",
                  background: "rgba(239,68,68,0.07)",
                  borderRadius: 8,
                  border: "1px solid rgba(239,68,68,0.2)",
                }}
              >
                {error}
              </div>
            )}

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Full Name *</label>
                <input
                  className="form-input"
                  placeholder="Priya Sharma"
                  value={form.name}
                  onChange={set("name")}
                  autoFocus
                  style={fieldErrors.name ? { borderColor: "var(--accent2)" } : {}}
                />
                {fieldErrors.name && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.name}</div>}
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={set("email")}
                  style={fieldErrors.email ? { borderColor: "var(--accent2)" } : {}}
                />
                {fieldErrors.email && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.email}</div>}
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Phone *</label>
                <input
                  className="form-input"
                  placeholder="9876543210"
                  value={form.phone}
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setForm((prev) => ({ ...prev, phone: val }));
                    setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  style={fieldErrors.phone ? { borderColor: "var(--accent2)" } : {}}
                />
                {fieldErrors.phone && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.phone}</div>}
              </div>
              <div>
                <label className="form-label">Qualification *</label>
                <select
                  className="form-input"
                  value={form.qualification}
                  onChange={set("qualification")}
                  style={fieldErrors.qualification ? { borderColor: "var(--accent2)" } : {}}
                >
                  <option value="">-- Select --</option>
                  {QUALIFICATIONS.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                {fieldErrors.qualification && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.qualification}</div>}
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 20 }}>
              <div>
                <label className="form-label">Location *</label>
                <input
                  className="form-input"
                  placeholder="City / State"
                  value={form.location}
                  onChange={set("location")}
                  style={fieldErrors.location ? { borderColor: "var(--accent2)" } : {}}
                />
                {fieldErrors.location && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.location}</div>}
              </div>
              <div>
                <label className="form-label">Preference *</label>
                <select
                  className="form-input"
                  value={form.preference}
                  onChange={set("preference")}
                  style={fieldErrors.preference ? { borderColor: "var(--accent2)" } : {}}
                >
                  <option value="">-- Select --</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
                {fieldErrors.preference && <div style={{ color: "var(--accent2)", fontSize: 11, marginTop: 3 }}>{fieldErrors.preference}</div>}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn-secondary"
                onClick={onClose}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? "Submitting..." : "Confirm Enrollment →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnrollModal;
